import React from 'react';
import { ResidencePage } from '../../components/Views/ResidencePage';
import {
  getCurrentResidenceQuery,
  residenceItemsQuery
} from '../../api/queries';
import { Layout } from '../../components/Layout/Layout';
import initApollo from '../../utils/init-Apollo';
import { getLocaleOnSSR } from '../../utils/helpers/getLocaleOnSSR';
import { getStaticData } from '../../utils/helpers/getStaticData';
import { getResidenceMockData } from '../../utils/helpers/getResidenceMockData';
import { normalizeItemPagePath } from '../../utils/helpers/item';
import { getSSRContextData } from '../../utils/helpers/ssrContext';
import { getResidenceSeoData } from '../../utils/helpers/seo';
import { withBugsnagGetServerSideProps } from '../../utils/withBugsnag';
import { extractApolloState } from '../../utils/helpers/extractApolloState';
import { shouldUseMock } from '../../utils/helpers/shouldUseMock';
import {
  CurrentResidenceQuery,
  ResidenceItemsQuery
} from '../../graphql/graphql';
import { Language, NextPageWithLayout, SEOData } from '../../utils/types';

type ResidencePageProps = {
  seoData: Partial<SEOData>;
  residence: CurrentResidenceQuery['residence'] | null;
  residenceItems: ResidenceItemsQuery['itemsConnection']['edges'];
  residenceSlug: string;
  hasError?: boolean;
  currentLang?: Language;
};

const Residence: NextPageWithLayout<ResidencePageProps> = props => (
  <ResidencePage {...props} />
);

export const getServerSideProps =
  withBugsnagGetServerSideProps<ResidencePageProps>(
    async ({ res, req, query, resolvedUrl }) => {
      const lang = getLocaleOnSSR(req);
      const { platform } = getSSRContextData(req);

      // Map the internal path from `resolvedUrl` to the public-facing URL
      // to ensure correct canonical links and other SEO data.
      // WARNING: Must sync with `next.config.js` rewrites!
      const asPath = normalizeItemPagePath(
        resolvedUrl.replace('/residences/', '/yasayis-kompleksleri/')
      );

      if (shouldUseMock(query)) {
        return getResidenceMockData(lang, asPath, req);
      }

      res.setHeader(
        'Cache-Control',
        'public, max-age=300, s-maxage=300, stale-while-revalidate=86400'
      );

      const apolloClient = initApollo({}, { lang, platform });

      const residenceSlug = query.residence as string;

      try {
        const [residenceRes] = await Promise.all([
          apolloClient.query({
            query: getCurrentResidenceQuery,
            variables: { slug: residenceSlug },
            errorPolicy: 'all'
          }),
          getStaticData(apolloClient)
        ]);

        const residence = residenceRes?.data?.residence ?? null;
        if (!residence) {
          res.statusCode = 404;
        }

        let residenceItems = [];

        const residenceId = residence?.id;

        if (residenceId) {
          try {
            const itemsRes = await apolloClient.query({
              query: residenceItemsQuery,
              variables: {
                first: 15,
                sort: 'BUMPED_AT_DESC',
                filter: {
                  residenceId,
                  rooms: ''
                }
              }
            });

            residenceItems = itemsRes?.data?.itemsConnection?.edges ?? [];
          } catch (error) {
            residenceItems = [];
            throw error;
          }
        }

        return extractApolloState(apolloClient, {
          props: {
            seoData: getResidenceSeoData(residence?.name, lang, asPath, req),
            residence,
            residenceSlug,
            residenceItems,
            hasError: Boolean(residenceRes?.errors?.length),
            currentLang: lang
          }
        });
      } catch (error) {
        // Preserve no-store cache headers on error and let wrapper report the error
        res.setHeader('Cache-Control', 'no-store');
        throw error;
      }
    },
    '/residences/[residence]',
    ctx => ({
      seoData: {},
      residence: null,
      residenceItems: [],
      hasError: true,
      residenceSlug: ctx.query.residence as string
    })
  );

Residence.getLayout = (page, pageProps) => (
  <Layout
    isBottomNavigation={false}
    isShowCloseButton={false}
    pageProps={pageProps}
    hideMobileHeader
  >
    {page}
  </Layout>
);

export default Residence;
